import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import roleService from "../services/role-service";
import logger from "../helpers/logger";

/**
 * Custom hook for accessing user roles
 * 
 * @returns {Object} Object containing user roles and helper functions
 */
export const useUserRoles = () => {
  const { instance, accounts } = useMsal();
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadRoles = useCallback(async () => {
    if (!accounts.length) {
      setRoles([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const userRoles = await roleService.getUserRoles(
        instance,
        accounts[0]
      );

      setRoles(userRoles);
      logger.log("User roles loaded:", userRoles);
    } catch (error) {
      logger.error("Error loading user roles:", error);
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  // Refresh roles when account changes
  const refreshRoles = useCallback(async () => {
    roleService.clearCache();
    await loadRoles();
  }, [loadRoles]);

  // Use local roles state for checks to ensure consistency with isLoading
  const hasRole = useCallback((role) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback((checkRoles) => checkRoles.some(role => roles.includes(role)), [roles]);
  const hasAllRoles = useCallback((checkRoles) => checkRoles.every(role => roles.includes(role)), [roles]);

  return {
    roles,
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    refreshRoles,
  };
};
